/**
 * Centralized Error Handling Service
 *
 * Provides consistent error handling across the application with:
 * - Error type classification
 * - User-friendly error messages
 * - Logging and monitoring integration
 * - i18n support
 */

// Import i18n conditionally to avoid test issues
let i18n;
try {
  i18n = require('../i18n').default;
} catch (e) {
  // i18n not available in test environment
  i18n = null;
}

// Error types for classification
export const ErrorType = {
  NETWORK: 'NETWORK',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  VALIDATION: 'VALIDATION',
  SERVER: 'SERVER',
  NOT_FOUND: 'NOT_FOUND',
  RATE_LIMIT: 'RATE_LIMIT',
  UNKNOWN: 'UNKNOWN',
};

// Error severity levels
export const ErrorSeverity = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
};

/**
 * Classify error based on HTTP status code and error details
 */
export const classifyError = (error) => {
  if (!error.response) {
    return {
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.ERROR,
    };
  }

  const status = error.response.status;

  if (status === 401) {
    return {
      type: ErrorType.AUTHENTICATION,
      severity: ErrorSeverity.WARNING,
    };
  }

  if (status === 403) {
    return {
      type: ErrorType.AUTHORIZATION,
      severity: ErrorSeverity.WARNING,
    };
  }

  if (status === 404) {
    return {
      type: ErrorType.NOT_FOUND,
      severity: ErrorSeverity.INFO,
    };
  }

  if (status === 422 || status === 400) {
    return {
      type: ErrorType.VALIDATION,
      severity: ErrorSeverity.WARNING,
    };
  }

  if (status === 429) {
    return {
      type: ErrorType.RATE_LIMIT,
      severity: ErrorSeverity.WARNING,
    };
  }

  if (status >= 500) {
    return {
      type: ErrorType.SERVER,
      severity: ErrorSeverity.ERROR,
    };
  }

  return {
    type: ErrorType.UNKNOWN,
    severity: ErrorSeverity.ERROR,
  };
};

/**
 * Get user-friendly error message with i18n support
 */
export const getUserFriendlyMessage = (error, errorType) => {
  // Check if error has a custom message from backend
  const backendMessage = error.response?.data?.error ||
                        error.response?.data?.detail ||
                        error.response?.data?.message;

  if (backendMessage && typeof backendMessage === 'string') {
    return backendMessage;
  }

  // Fallback to predefined messages based on error type
  const messageKey = `errors.${errorType.toLowerCase()}`;
  const fallbackMessages = {
    [ErrorType.NETWORK]: 'Network connection error. Please check your internet connection.',
    [ErrorType.AUTHENTICATION]: 'Authentication required. Please log in.',
    [ErrorType.AUTHORIZATION]: 'You do not have permission to perform this action.',
    [ErrorType.VALIDATION]: 'Invalid data provided. Please check your input.',
    [ErrorType.SERVER]: 'Server error. Please try again later.',
    [ErrorType.NOT_FOUND]: 'The requested resource was not found.',
    [ErrorType.RATE_LIMIT]: 'Too many requests. Please try again later.',
    [ErrorType.UNKNOWN]: 'An unexpected error occurred.',
  };

  // Try to get translated message, fallback to English
  const translatedMessage = i18n && i18n.exists && i18n.exists(messageKey)
    ? i18n.t(messageKey)
    : fallbackMessages[errorType];

  return translatedMessage || fallbackMessages[ErrorType.UNKNOWN];
};

/**
 * Extract validation errors from response
 */
export const extractValidationErrors = (error) => {
  const data = error.response?.data;

  if (!data) return null;

  // Handle different validation error formats
  if (data.errors && typeof data.errors === 'object') {
    return data.errors;
  }

  if (data.field_errors && typeof data.field_errors === 'object') {
    return data.field_errors;
  }

  return null;
};

/**
 * Log error to monitoring service (e.g., Sentry, LogRocket)
 */
export const logError = (error, context = {}) => {
  const errorInfo = {
    message: error.message,
    stack: error.stack,
    response: error.response?.data,
    status: error.response?.status,
    url: error.config?.url,
    method: error.config?.method,
    context,
    timestamp: new Date().toISOString(),
  };

  // In development, log to console
  if (process.env.NODE_ENV === 'development') {
    console.error('[Error Handler]', errorInfo);
  }

  // TODO: Integrate with external error tracking service
  // Example: Sentry.captureException(error, { extra: errorInfo });
};

/**
 * Determine if error should be shown to user
 */
export const shouldShowToUser = (errorType, severity) => {
  // Don't show info-level errors as toasts
  if (severity === ErrorSeverity.INFO) {
    return false;
  }

  // Don't show authentication errors (handled by auth flow)
  if (errorType === ErrorType.AUTHENTICATION) {
    return false;
  }

  return true;
};

/**
 * Main error handler function
 */
export const handleError = (error, options = {}) => {
  const {
    showToast = true,
    logToConsole = true,
    context = {},
    customMessage = null,
  } = options;

  // Classify error
  const { type, severity } = classifyError(error);

  // Log error
  if (logToConsole) {
    logError(error, { ...context, type, severity });
  }

  // Get user-friendly message
  const message = customMessage || getUserFriendlyMessage(error, type);

  // Extract validation errors if present
  const validationErrors = type === ErrorType.VALIDATION
    ? extractValidationErrors(error)
    : null;

  return {
    type,
    severity,
    message,
    validationErrors,
    originalError: error,
    shouldShowToUser: shouldShowToUser(type, severity) && showToast,
  };
};

/**
 * Check if error is retryable
 */
export const isRetryableError = (error) => {
  // Don't retry client errors (4xx except 429)
  const status = error.response?.status;

  if (status >= 400 && status < 500 && status !== 429) {
    return false;
  }

  // Retry network errors, 5xx errors, and rate limits
  return !error.response || status >= 500 || status === 429;
};

/**
 * Get retry delay based on attempt number (exponential backoff)
 */
export const getRetryDelay = (attemptNumber, baseDelay = 1000) => {
  const delay = baseDelay * Math.pow(2, attemptNumber - 1);
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 500;
  return Math.min(delay + jitter, 10000); // Max 10 seconds
};

const errorHandlerService = {
  ErrorType,
  ErrorSeverity,
  classifyError,
  getUserFriendlyMessage,
  extractValidationErrors,
  logError,
  shouldShowToUser,
  handleError,
  isRetryableError,
  getRetryDelay,
};

export default errorHandlerService;
